import { createContext, HTMLAttributes, useContext, useMemo, useRef } from 'react';
import { joinClassNames } from '../../utils';
import styles from './HelpBanner.module.scss';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import { Button, ButtonProps } from '../Button';
import { CSSTransition } from 'react-transition-group';
import { AnimatedDiv } from '../../internalComponents/AnimatedDiv';
import { HelpBannerVariant } from './enums';

export const HelpBannerContent = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const classNames = joinClassNames(styles.helpBannerContent, className);

  return (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
};

export const HelpBannerVideo = ({
  className,
  url,
  ...props
}: HTMLAttributes<HTMLDivElement> & ReactPlayerProps) => {
  const classNames = joinClassNames(styles.helpBannerVideo, className);

  return <ReactPlayer className={classNames} url={url} width="480" height="270" {...props} />;
};

const HelpBannerContext = createContext<{
  isHidden: boolean;
  toggleHidden: () => void;
}>({
  isHidden: false,
  toggleHidden: () => {},
});

type HelpBannerProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * Boolean to indicate that the banner should be hidden. It is recommended
   * to use the `usePersistentIsHidden` hook to manage banners that should be
   * able to be permanently dismissed (isHidden is then saved to localstorage).
   */
  isHidden: boolean;
  /** Function to toggle the hidden state */
  toggleHidden: () => void;
  /**
   * Duration of transition in milliseconds
   * @default 300
   */
  transitionDuration?: number;
  /**
   * Style variant of the HelpBanner
   * @default HelpBannerVariant.Primary
   */
  variant?: HelpBannerVariant;
};

export const HelpBanner = ({
  children,
  className,
  isHidden,
  toggleHidden,
  transitionDuration,
  variant = HelpBannerVariant.Primary,
  ...props
}: HelpBannerProps) => {
  const nodeRef = useRef(null);
  const classNames = joinClassNames(styles.helpBanner, styles[variant], className);

  const value = useMemo(
    () => ({ isHidden, toggleHidden, transitionDuration: transitionDuration ?? 300 }),
    [isHidden, toggleHidden, transitionDuration]
  );

  return (
    <HelpBannerContext.Provider value={value}>
      <CSSTransition
        exit
        unmountOnExit
        in={!isHidden}
        timeout={value.transitionDuration}
        nodeRef={nodeRef}
        classNames={{
          exit: styles['animatedContainer-hidden'],
          exitDone: styles['animatedContainer-hidden'],
        }}
      >
        <AnimatedDiv
          className={styles.animatedContainer}
          transitionDuration={value.transitionDuration}
          ref={nodeRef}
        >
          <div className={classNames} {...props}>
            {children}
          </div>
        </AnimatedDiv>
      </CSSTransition>
    </HelpBannerContext.Provider>
  );
};

export const HelpBannerToggle = ({ children, ...props }: Omit<ButtonProps, 'onClick'>) => {
  const { toggleHidden } = useContext(HelpBannerContext);

  return (
    <Button onClick={toggleHidden} {...props}>
      {children}
    </Button>
  );
};
